'use client';

import { useIntersectionObserver } from '@packages/shared/src/hooks/useIntersectionObserver/useIntersectionObserver';
import type { RefObject } from 'react';

type SeenObserverProps = {
  /** The card element to watch */
  cardRef: RefObject<Element | null>;
  sku: string;
  onFirstBecameVisible: (sku: string) => void;
};

/**
 * Reports a reco card as seen, once it has been fully visible in the viewport for the first time.
 *
 * This is a separate component instead of a hook inside the card, so that it can be rendered
 * conditionally: cards whose consumer does not need seen tracking never call
 * `useIntersectionObserver` at all, and no `IntersectionObserver` is created for them.
 */
export const SeenObserver = ({ cardRef, sku, onFirstBecameVisible }: SeenObserverProps) => {
  useIntersectionObserver(cardRef, {
    intersectOnce: true,
    // count the card only when it is fully visible, same threshold as the search product list
    threshold: 1,
    callback: (isIntersecting) => {
      // the observer reports the initial state right after observing, so cards that are still
      // off-screen call back with `isIntersecting: false` first
      if (!isIntersecting) return;

      onFirstBecameVisible(sku);
    },
  });

  return null;
};
